import { useState } from 'react';
import { Carte, Chargement, EnteteEcran, MessageErreur, Pastille } from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { FormulaireQuestionnaire } from '../components/questionnaires/FormulaireQuestionnaire';
import { ErreurApi } from '../lib/api';
import {
  useMesQuestionnaires,
  useRepondreQuestionnaire,
  type QuestionnaireARemplir,
} from '../lib/useQuestionnaires';

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * « Mes questionnaires » (CDC §8.2) : satisfaction à 2 et 6 mois et en fin de
 * parcours, insertion à 3 et 6 mois après l'examen. Ils arrivent d'eux-mêmes,
 * aussi par mail avec un lien personnel ; chacun ne se remplit qu'une fois.
 */
export function AppQuestionnairesPage(): JSX.Element {
  const notifier = useToast();
  const { data, isLoading, error } = useMesQuestionnaires();
  const repondre = useRepondreQuestionnaire();
  const [ouvert, setOuvert] = useState<QuestionnaireARemplir | null>(null);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Mes questionnaires"
        sousTitre="Espace apprenant · satisfaction et insertion, envoyés automatiquement"
      />

      {isLoading && <Chargement />}
      {error instanceof ErreurApi && <MessageErreur message={error.message} />}

      {ouvert ? (
        <Carte titre={ouvert.titre} sansBordure>
          <div className="p-4 sm:p-6">
            <FormulaireQuestionnaire
              questionnaire={ouvert}
              enCours={repondre.isPending}
              erreur={repondre.error instanceof ErreurApi ? repondre.error.message : null}
              onEnvoyer={(reponses) =>
                repondre.mutate(
                  { id: ouvert.id, reponses },
                  {
                    onSuccess: ({ message }) => {
                      notifier(message);
                      setOuvert(null);
                    },
                  },
                )
              }
            />
          </div>
        </Carte>
      ) : (
        data && (
          <Carte titre="Questionnaires reçus">
            {data.length === 0 && (
              <div className="px-4 py-8 text-sm text-codialis-gray sm:px-6">
                Aucun questionnaire pour le moment : ils vous parviennent au fil de votre parcours.
              </div>
            )}
            {data.map((questionnaire) => (
              <div key={questionnaire.id} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-codialis-navy">{questionnaire.titre}</div>
                  <div className="text-[11px] text-codialis-gray">
                    Reçu le {jour(questionnaire.envoyeLe)}
                    {questionnaire.repondLe ? ` · rempli le ${jour(questionnaire.repondLe)}` : ''}
                  </div>
                </div>
                {questionnaire.repondLe ? (
                  <Pastille texte="Rempli" ton="success" />
                ) : (
                  <button
                    type="button"
                    onClick={() => setOuvert(questionnaire)}
                    className="min-h-[44px] rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
                  >
                    Répondre
                  </button>
                )}
              </div>
            ))}
          </Carte>
        )
      )}
    </div>
  );
}
